import React from 'react';
import styled from 'styled-components';
import { SxProps } from '@mui/material';
import Button from '../Button';
import { Flex } from '../Flex';

const FileUploadWrapper = styled(Flex)`
  position: relative;
  height: 100%;
  :hover {
    background: ${({ theme }) => theme.palette.common.gray};
  }
`;

const FileInputWrapper = styled(Flex)`
  align-items: center;
  border: 1px solid ${({ theme }) => theme.palette.common.gray1};
  border-radius: 8px;
  height: 40px;
  overflow: hidden;
`;

export const FileInputLabel = styled.input`
  width: 100%;
  font-size: 14px !important;
  padding: 0 14px;
  border: none;
  background: none;
  color: ${({ theme }) => theme.palette.common.black};
  font-weight: ${({ value, theme }) =>
    value ? theme.typography.fontWeightBold : 400};
`;

export const FileButton = styled(Button)<{ component?: string }>`
  &.MuiButton-root {
    height: 100%;
    width: 90x;
    box-shadow: none;
    background: none;
    color: ${({ theme }) => theme.palette.common.black};
    overflow: hidden;
    border: 1px solid ${({ theme }) => theme.palette.common.black};
    border-radius: 0 8px 8px 0;
    :hover {
      background: none;
      cursor: pointer;
    }
  }
`;

export const FileInput = styled.input`
  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 0;
`;

export type SimpleFile = { name: string; content: string };

type InputValue = SimpleFile | File | null;

type Props = {
  value: InputValue;
  onChange: (value: InputValue) => void;
  placeholder: string;
  sx?: SxProps;
  storeFileObject?: boolean;
};

const FileUploadInput = React.forwardRef(
  (
    {
      value = { name: '', content: '' },
      onChange,
      placeholder,
      sx,
      storeFileObject = false,
    }: Props,
    ref: any,
  ) => {
    const readText = async (event: any) => {
      event.preventDefault();

      if (!event.target.files || !event.target.files[0]) {
        return;
      }

      if (storeFileObject) {
        onChange(event.target.files[0]);
        return;
      }

      const reader = new FileReader();

      reader.onload = async (e: any) => {
        const name = event.target.files[0]?.name;
        const content = e.target.result;

        onChange({ name, content });
      };

      reader.readAsText(event.target.files[0]);
    };

    const displayName =
      value instanceof File
        ? value.name
        : value && typeof value === 'object' && 'name' in value
        ? value.name
        : '';

    return (
      <FileInputWrapper ref={ref} sx={{ width: '100%', ...sx }}>
        <FileInputLabel
          disabled
          placeholder={placeholder}
          value={displayName}
        />
        <FileUploadWrapper>
          <FileButton disableElevation variant="outlined" component="label">
            Browse
            <FileInput
              type="file"
              hidden
              onChange={(e) => readText(e)}
              onBlur={(e) => e.preventDefault()}
            />
          </FileButton>
        </FileUploadWrapper>
      </FileInputWrapper>
    );
  },
);

export default FileUploadInput;
